import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { LogOut, Plus, List, Users } from 'lucide-react';
import { promotionsAPI, leadsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_84e14be8-c66b-4e50-b378-639bfe417cdf/artifacts/vn03bb90_297950681_1043688306290539_3612407571660205954_n.jpg';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Not authenticated');
      const userData = await res.json();
      setUser(userData);
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.user) return;
    checkAuth();
  }, [checkAuth, location.state]);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore
    }
    toast.success('Logout realizado!');
    navigate('/admin/login');
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A8B5]"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-white border-r border-slate-200">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Rota Continental" className="h-10 w-10 rounded-lg object-cover" />
            <span className="text-xl font-bold">Admin</span>
          </div>

          {user && (
            <div className="mb-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          )}

          <nav className="space-y-2">
            <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-100 transition" data-testid="nav-dashboard">
              <List className="w-5 h-5" /><span>Promoções</span>
            </Link>
            <Link to="/admin/leads" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-100 transition" data-testid="nav-leads">
              <Users className="w-5 h-5" /><span>Leads</span>
            </Link>
          </nav>

          <div className="mt-8">
            <Button onClick={handleLogout} variant="outline" className="w-full justify-start" data-testid="logout-button">
              <LogOut className="w-5 h-5 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-8">
        <Routes>
          <Route path="/" element={<Navigate to="/admin/dashboard" />} />
          <Route path="/dashboard" element={<PromotionsList />} />
          <Route path="/leads" element={<LeadsList />} />
        </Routes>
      </main>
    </div>
  );
};

const PromotionsList = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPromotions = useCallback(async () => {
    try {
      const response = await promotionsAPI.getAll();
      setPromotions(response.data);
    } catch {
      toast.error('Erro ao carregar promoções');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente deletar esta promoção?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success('Promoção deletada!');
      loadPromotions();
    } catch {
      toast.error('Erro ao deletar promoção');
    }
  };

  if (loading) return <div className="text-center py-12">Carregando...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-slate-900" data-testid="promotions-title">Promoções</h1>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Rota</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Companhia</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Preço</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {promotions.map((promo) => (
              <tr key={promo.id} className="hover:bg-slate-50 transition" data-testid={`promo-row-${promo.id}`}>
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-900">{promo.origin} → {promo.destination}</div>
                  <div className="text-sm text-slate-600">{promo.cabin} • {promo.trip_type}</div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-700">{promo.airline}</td>
                <td className="px-6 py-4 text-sm font-semibold text-[#00A8B5]">R$ {promo.calculated_price.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <Button onClick={() => handleDelete(promo.id)} variant="destructive" size="sm" data-testid={`delete-promo-${promo.id}`}>Deletar</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setLeads(data);
    } catch {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  if (loading) return <div className="text-center py-12">Carregando...</div>;

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-8" data-testid="leads-title">Leads Recebidos</h1>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Rota</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Contato</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Datas</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Info</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {leads.map((lead) => (
              <tr key={lead.id} className="hover:bg-slate-50 transition" data-testid={`lead-row-${lead.id}`}>
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-900">{lead.origin} → {lead.destination}</div>
                  <div className="text-sm text-slate-600">{lead.passengers} passageiro(s)</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-900">{lead.email}</div>
                  <div className="text-sm text-slate-600">{lead.whatsapp}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-700">Ida: {new Date(lead.departure_date).toLocaleDateString('pt-BR')}</div>
                  {lead.return_date && <div className="text-sm text-slate-700">Volta: {new Date(lead.return_date).toLocaleDateString('pt-BR')}</div>}
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1 text-xs text-slate-600">
                    {lead.flexible_dates && <div>✓ Datas flexíveis</div>}
                    {lead.checked_baggage && <div>✓ Com bagagem</div>}
                    {lead.travel_insurance && <div>✓ Com seguro</div>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
